import { useId, type RefObject } from "react";
import type { ConsoleLine } from "./usePythonRuntime";
import type { IdeOutputTab } from "./protocol";
import { TurtleStage } from "./TurtleStage";

export type IdeOutputPanelProps = {
  readonly lines: readonly ConsoleLine[];
  readonly phase: string;
  readonly inputPrompt: string;
  readonly inputDraft: string;
  readonly onInputDraftChange: (value: string) => void;
  readonly onSubmitInput: () => void;
  readonly consoleInputRef: RefObject<HTMLInputElement | null>;
  readonly clearConsoleOnRun: boolean;
  readonly onClearConsoleOnRunChange: (checked: boolean) => void;
  readonly outputTab: IdeOutputTab;
  readonly onOutputTabChange: (tab: IdeOutputTab) => void;
  readonly turtleHtml: string | null;
  readonly turtleTabVisible: boolean;
};

export function IdeOutputPanel({
  lines,
  phase,
  inputPrompt,
  inputDraft,
  onInputDraftChange,
  onSubmitInput,
  consoleInputRef,
  clearConsoleOnRun,
  onClearConsoleOnRunChange,
  outputTab,
  onOutputTabChange,
  turtleHtml,
  turtleTabVisible,
}: IdeOutputPanelProps) {
  const consoleInputId = useId();
  const showConsole = outputTab === "console" || !turtleTabVisible;
  const showTurtle = outputTab === "turtle" && turtleTabVisible;

  return (
    <section className="ide-console-panel" aria-label="Program output">
      <div className="ide-console-head">
        <div className="ide-output-tabs" role="tablist" aria-label="Output views">
          <button
            type="button"
            role="tab"
            id="ide-output-tab-console"
            aria-selected={showConsole}
            aria-controls="ide-output-panel-console"
            className={`ide-output-tab${showConsole ? " ide-output-tab--active" : ""}`}
            onClick={() => onOutputTabChange("console")}
          >
            Console
          </button>
          {turtleTabVisible ? (
            <button
              type="button"
              role="tab"
              id="ide-output-tab-turtle"
              aria-selected={showTurtle}
              aria-controls="ide-output-panel-turtle"
              className={`ide-output-tab${showTurtle ? " ide-output-tab--active" : ""}`}
              onClick={() => onOutputTabChange("turtle")}
            >
              Turtle
            </button>
          ) : null}
        </div>
        {showConsole ? (
          <label className="ide-console-option">
            <input
              type="checkbox"
              checked={clearConsoleOnRun}
              onChange={(e) => onClearConsoleOnRunChange(e.target.checked)}
            />
            Clear before each run
          </label>
        ) : null}
      </div>

      <div
        id="ide-output-panel-console"
        role="tabpanel"
        aria-labelledby="ide-output-tab-console"
        hidden={!showConsole}
        className="ide-output-panel-body"
      >
        <pre className="ide-console-output">
          {lines.map((line, i) => (
            <span
              key={`${i}-${line.text.slice(0, 24)}`}
              className={`ide-console-line ide-console-line--${line.stream}`}
            >
              {line.text}
              {"\n"}
            </span>
          ))}
        </pre>
        {phase === "awaiting_input" ? (
          <form
            className="ide-console-input-row"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmitInput();
            }}
          >
            <label className="ide-input-label" htmlFor={consoleInputId}>
              {inputPrompt ? `Input: ${inputPrompt}` : "Input"}
            </label>
            <div className="ide-input-inline">
              <input
                ref={consoleInputRef}
                id={consoleInputId}
                className="ide-input-field input-rounded"
                type="text"
                autoComplete="off"
                value={inputDraft}
                onChange={(e) => onInputDraftChange(e.target.value)}
                placeholder="Type a line, then Enter or Submit"
              />
              <button type="submit" className="btn primary ide-btn tight">
                Submit
              </button>
            </div>
            <p className="muted small ide-input-hint">
              Or press Stop in the header to cancel the program while waiting.
            </p>
          </form>
        ) : null}
      </div>

      {turtleTabVisible ? (
        <div
          id="ide-output-panel-turtle"
          role="tabpanel"
          aria-labelledby="ide-output-tab-turtle"
          hidden={!showTurtle}
          className="ide-output-panel-body ide-turtle-panel"
        >
          {turtleHtml ? (
            <TurtleStage html={turtleHtml} />
          ) : (
            <p className="ide-turtle-empty muted">Run code that uses <code>import turtle</code> to see a drawing here.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
